import React from 'react';
import { Ruler, Box, Trash2, X, CheckCircle } from 'lucide-react';
import { distance, polygonArea, formatLength, formatArea } from '../utils/geometry';

/**
 * MeasureResultDialog - Dialog zur Auswertung der Messung
 *
 * Zeigt die Messergebnisse und bietet Optionen:
 * - Fläche zu Raum konvertieren
 * - Punkte behalten
 * - Alles verwerfen
 */
export function MeasureResultDialog({
  measurePoints = [],
  onConvertToSpace,
  onKeepPoints,
  onDiscard,
  onClose,
}) {
  // Berechnungen
  const segments = [];
  let totalLength = 0;

  for (let i = 0; i < measurePoints.length - 1; i++) {
    const p1 = measurePoints[i];
    const p2 = measurePoints[i + 1];
    const dist = distance(p1, p2);
    segments.push({
      index: i + 1,
      distance: dist,
    });
    totalLength += dist;
  }

  const canCreateSpace = measurePoints.length >= 3;
  const area = canCreateSpace ? polygonArea(measurePoints) : 0;
  const areaM2 = area / 1000000;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-96 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="bg-green-600 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ruler size={20} />
            <span className="font-semibold">Messung abgeschlossen</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-green-500 rounded"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Zusammenfassung */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Messpunkte:</span>
              <span className="font-bold">{measurePoints.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Gesamtlänge:</span>
              <span className="font-bold text-green-600">{formatLength(totalLength)} m</span>
            </div>
            {canCreateSpace && (
              <div className="flex justify-between items-center border-t pt-2">
                <span className="text-gray-600">Fläche:</span>
                <span className="font-bold text-green-600">{areaM2.toFixed(2)} m²</span>
              </div>
            )}
          </div>

          {/* Segmente */}
          {segments.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600 uppercase">
                Strecken
              </div>
              <div className="max-h-32 overflow-y-auto">
                {segments.map((seg) => (
                  <div
                    key={seg.index}
                    className="flex justify-between items-center px-3 py-1.5 text-sm border-b last:border-b-0 hover:bg-gray-50"
                  >
                    <span className="text-gray-500">Strecke {seg.index}:</span>
                    <span className="font-mono">{formatLength(seg.distance)} m</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Aktionen */}
          <div className="space-y-2 pt-2">
            {/* Zu Raum konvertieren */}
            {canCreateSpace && (
              <button
                onClick={onConvertToSpace}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
              >
                <Box size={18} />
                Als Raum erstellen ({areaM2.toFixed(2)} m²)
              </button>
            )}

            {/* Punkte behalten */}
            <button
              onClick={onKeepPoints}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg font-medium transition-colors"
            >
              <CheckCircle size={18} />
              Punkte behalten (ohne Raum)
            </button>

            {/* Verwerfen */}
            <button
              onClick={onDiscard}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 rounded-lg transition-colors"
            >
              <Trash2 size={18} />
              Messung verwerfen
            </button>
          </div>

          {/* Hilfe-Text */}
          <p className="text-xs text-gray-400 text-center">
            Tipp: Drücke ESC um die Messung abzubrechen
          </p>
        </div>
      </div>
    </div>
  );
}
